import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Cart, CartItem } from '../../../../models/cart/cart';
import { getMerchantName, getProductImageUrl } from '../../../../models/product/product';
import { ROUTES } from '../../../../constants/routes';

interface CartSidebarProps {
  cart: Cart;
  isOpen: boolean;
  onClose: () => void;
  onUpdateQuantity: (bookId: number, quantity: number) => void;
  onRemoveItem: (bookId: number) => void;
  isRTL: boolean;
}

export const CartSidebar: React.FC<CartSidebarProps> = ({
  cart,
  isOpen,
  onClose,
  onUpdateQuantity,
  onRemoveItem,
  isRTL
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleCheckout = () => {
    navigate(ROUTES.CHECKOUT, { state: { cart } });
    onClose();
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40"
          onClick={handleBackdropClick}
        />
      )}
      
      {/* Sidebar */}
      <div
        className={`fixed top-0 ${isRTL ? 'left-0' : 'right-0'} h-full w-96 bg-white shadow-2xl transform transition-transform duration-300 ease-in-out z-50 flex flex-col ${
          isOpen ? 'translate-x-0' : isRTL ? '-translate-x-full' : 'translate-x-full'
        }`}
        style={{
          top: '80px', // Space from header
          bottom: '60px', // Space from footer
          height: 'calc(100vh - 140px)', // Total height minus header and footer space
          marginTop: '20px', // Additional top margin
          marginBottom: '20px', // Additional bottom margin
          marginRight: isRTL ? '0' : '20px', // Right margin for LTR
          marginLeft: isRTL ? '20px' : '0', // Left margin for RTL
          borderRadius: '16px'
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-white rounded-t-2xl">
          <div className={`flex items-center ${isRTL ? 'space-x-reverse' : ''} space-x-3`}>
            <svg className="w-6 h-6 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4m1.6 8L5 3H3m4 10v6a1 1 0 001 1h10a1 1 0 001-1v-6M9 19a1 1 0 102 0 1 1 0 00-2 0zm8 0a1 1 0 102 0 1 1 0 00-2 0z" />
            </svg>
            <h2 className="text-xl font-bold text-gray-900">
              {t('cart.title', 'Shopping Cart')}
            </h2>
            {cart.itemCount > 0 && (
              <span className="bg-black text-white text-sm rounded-full w-6 h-6 flex items-center justify-center font-bold">
                {cart.itemCount}
              </span>
            )}
          </div>
          
          {/* Collapse Button */}
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors group"
            title={t('cart.close', 'Close cart')}
          >
            <svg 
              className={`w-5 h-5 text-gray-600 group-hover:text-black transition-transform ${
                isRTL ? 'rotate-180' : ''
              }`} 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        {/* Cart Content */}
        <div className="flex-1 overflow-y-auto">
          {cart.items.length === 0 ? (
            /* Empty State */
            <div className="flex flex-col items-center justify-center h-full p-6 text-center">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4m1.6 8L5 3H3m4 10v6a1 1 0 001 1h10a1 1 0 001-1v-6M9 19a1 1 0 102 0 1 1 0 00-2 0zm8 0a1 1 0 102 0 1 1 0 00-2 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {t('cart.empty.title', 'Your cart is empty')}
              </h3>
              <p className="text-gray-600 mb-4">
                {t('cart.empty.message', 'Add some books to get started!')}
              </p>
              <button
                onClick={onClose}
                className="bg-black text-white px-6 py-2 rounded-lg hover:bg-gray-800 transition-colors"
              >
                {t('cart.empty.continue-shopping', 'Continue Shopping')}
              </button>
            </div>
          ) : (
            /* Cart Items */
            <div className="p-4 space-y-4">
              {cart.items.map((item: CartItem) => (
                <div key={`${item.product.id}-${item.product.type}`} className="bg-gray-50 rounded-lg p-4">
                  <div className={`flex gap-4 ${isRTL ? 'flex-row-reverse' : 'flex-row'}`}>
                    {/* Book Cover */}
                    <div className="w-16 h-20 bg-white rounded-lg overflow-hidden flex-shrink-0 shadow-sm">
                      <img
                        src={getProductImageUrl(item.product)}
                        alt={item.product.name}
                        className="w-full h-full object-cover"
                      />
                    </div>

                    {/* Book Details */}
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-gray-900 line-clamp-2 mb-1">
                        {item.product.name}
                      </h4>
                      <p className="text-sm text-gray-600 mb-2">
                        {t('products.books.by-author', 'by')} {getMerchantName(item.product)}
                      </p>
                      
                      {/* Format */}
                      <div className="flex items-center space-x-2 mb-2">
                        <span className={`text-xs px-2 py-1 rounded`}>
                        </span>
                      </div>

                      {/* Price and Quantity Controls */}
                      <div className={`flex items-center justify-between ${isRTL ? 'space-x-reverse' : ''}`}>
                        <div className="flex items-center space-x-3">
                          {/* Quantity Controls */}
                          <div className="flex items-center bg-white rounded-lg border border-gray-200">
                            <button
                              onClick={() => onUpdateQuantity(item.product.id, item.quantity - 1)}
                              className="p-1 hover:bg-gray-50 rounded-l-lg transition-colors"
                              disabled={item.quantity <= 1}
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                              </svg>
                            </button>
                            <span className="px-3 py-1 text-sm font-medium min-w-[2rem] text-center">
                              {item.quantity}
                            </span>
                            <button
                              onClick={() => onUpdateQuantity(item.product.id, item.quantity + 1)}
                              className="p-1 hover:bg-gray-50 rounded-r-lg transition-colors"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                              </svg>
                            </button>
                          </div>

                          {/* Price */}
                          <div className="text-sm">
                            <span className="font-bold text-black">
                              {(item.product.price * item.quantity).toFixed(0)} EGP
                            </span>
                            {item.quantity > 1 && (
                              <div className="text-xs text-gray-500">
                                {item.product.price.toFixed(0)} EGP × {item.quantity}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Remove Button */}
                        <button
                          onClick={() => onRemoveItem(item.product.id)}
                          className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                          title={t('cart.remove-item', 'Remove item')}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer with Total and Checkout */}
        {cart.items.length > 0 && (
          <div className="border-t border-gray-200 p-6 bg-white rounded-b-2xl">
            {/* Order Summary */}
            <div className="space-y-3 mb-4">
              <div className={`flex justify-between text-sm ${isRTL ? 'space-x-reverse' : ''}`}>
                <span className="text-gray-600">{t('cart.subtotal', 'Subtotal')}:</span>
                <span className="font-medium">{cart.total.toFixed(2)} {t('currency.egp', 'EGP')}</span>
              </div>
              <div className={`flex justify-between text-sm ${isRTL ? 'space-x-reverse' : ''}`}>
                <span className="text-gray-600">{t('cart.shipping', 'Shipping')}:</span>
                <span className="font-medium text-gray-600">{t('cart.shipping-tbd', 'To be determined')}</span>
              </div>
              <div className={`flex justify-between text-lg font-bold pt-3 border-t border-gray-200 ${isRTL ? 'space-x-reverse' : ''}`}>
                <span>{t('cart.total', 'Total')}:</span>
                <span>{cart.total.toFixed(2)} {t('currency.egp', 'EGP')}</span>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              <button
                onClick={handleCheckout}
                className="w-full bg-black text-white py-3 px-4 rounded-lg font-semibold hover:bg-gray-800 transition-colors flex items-center justify-center space-x-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <span>{t('cart.checkout', 'Proceed to Checkout')}</span>
              </button>
              
              <button
                onClick={onClose}
                className="w-full bg-gray-100 text-gray-800 py-3 px-4 rounded-lg font-medium hover:bg-gray-200 transition-colors"
              >
                {t('cart.continue-shopping', 'Continue Shopping')}
              </button>
            </div>

            {/* Security Badge */}
            <div className={`flex items-center justify-center mt-4 text-xs text-gray-500 ${isRTL ? 'space-x-reverse' : ''} space-x-2`}>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              <span>{t('cart.secure-checkout', 'Secure checkout guaranteed')}</span>
            </div>
          </div>
        )}

        {/* Mobile-specific spacing adjustments */}
        <style>{`
          @media (max-width: 640px) {
            .cart-sidebar {
              width: 100vw !important;
              margin: 0 !important;
              border-radius: 0 !important;
              top: 80px !important;
              bottom: 0 !important;
              height: calc(100vh - 80px) !important;
            }
          }
        `}</style>
      </div>
    </>
  );
};